import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import axios from "infra/http";
import { Button, Flex, Spinner } from "@chakra-ui/react";
import { BsCardImage } from "react-icons/bs";
import colors from "core/resources/theme/colors";
import { IoClose } from "react-icons/io5";
import ButtonAnexarArquivos from "components/Button/ButtonAnexarArquivos";
import { AiOutlineCamera } from "react-icons/ai";
import { FaExternalLinkAlt } from "react-icons/fa";
import ModalCamera from "components/ModalCamera";
import base64ToFile from "infra/helpers/base64ToFile";

export const Container = styled(Flex)`
  padding: 0 8px;
  background-color: #eff4ff;
  border-radius: 8px;

  img {
    margin: 8px 0;
    border-radius: 8px;
  }
`;

interface UploadImagemProps {
  value: any;
  onChange?: (arg: string | null) => any;
  onArquivoId?: (id: string | null) => any;
  isTenant?: boolean;
  confirmButton?: string;
  height?: string | number;
  onUploadChange?: (uploading: boolean) => void;
  mode?: "confirm" | "camera";
}

function UploadImagem({
  value,
  onChange,
  onArquivoId,
  isTenant,
  confirmButton,
  height,
  onUploadChange,
  mode = "confirm",
}: UploadImagemProps) {
  const [uploading, setUploading] = useState(false);
  const [fileInfo, setFileInfo] = useState(null);
  const [modalCameraOpen, setModalCameraOpen] = useState(false);

  const refUploadPerfil = useRef<any>();

  useEffect(() => {
    setFileInfo(value);
  }, [value]);

  const handleUpload = useCallback(
    async (file: File) => {
      const formData = new FormData();

      formData.append("file", file);

      try {
        setUploading(true);
        onUploadChange?.(true);
        const { data } = await axios.post(
          `/v1/arquivos/upload-${isTenant ? "tenant" : "perfil"}`,
          formData
        );
        onChange?.(data.data);
        onArquivoId?.(data.id);
      } catch (e) {
        onChange?.(null);
        onArquivoId?.(null);
      } finally {
        onUploadChange?.(false);
        setUploading(false);
      }
    },
    [onChange, isTenant, onUploadChange, onArquivoId]
  );

  return (
    <Container position="relative" flexDir="column" minH={height}>
      <Flex
        py={fileInfo ? "8px" : "32px"}
        flex={1}
        justifyContent="center"
        alignItems="center"
        position="relative"
      >
        {fileInfo ? (
          <>
            <Flex
              as={"img"}
              src={fileInfo}
              h="100%"
              maxH="100px"
              w="auto"
              cursor="pointer"
              onClick={() => {
                window.open(fileInfo, "_blank");
              }}
            />

            <Button
              position="absolute"
              right="-8px"
              top="-8px"
              isDisabled={uploading}
              size="sm"
              colorScheme="red"
              rounded="full"
              p="0"
              w="30px"
              h="30px"
              onClick={() => {
                onChange?.(null);
                onArquivoId?.(null);
              }}
            >
              <IoClose fontSize="14px" />
            </Button>
          </>
        ) : (
          <BsCardImage color={colors.primary} fontSize="40px" />
        )}
        {uploading ? (
          <Flex
            h="40px"
            w="40px"
            alignSelf="center"
            position="absolute"
            margin="auto"
            bg="rgba(255,255,255, 0.4)"
            borderRadius="full"
            justifyContent="center"
            alignItems="center"
          >
            <Spinner size="xs" color={colors.primary} />
          </Flex>
        ) : null}
      </Flex>

      <input
        type="file"
        ref={refUploadPerfil}
        style={{ display: "none" }}
        accept="image/*"
        onChange={(ev) => {
          if (ev.target.files) {
            handleUpload(ev.target.files[0]);
          }
        }}
      />
      {mode === "confirm" ? (
        <Button
          isFullWidth
          isLoading={uploading}
          isDisabled={uploading}
          size="xs"
          mb={"16px"}
          colorScheme="blue"
          variant="outline"
          onClick={() => refUploadPerfil.current.click()}
        >
          {fileInfo ? "Trocar imagem" : confirmButton ?? "Procurar"}
        </Button>
      ) : null}

      {mode === "camera" ? (
        <Flex gap={1} justifyContent="center" alignItems="center" mb={2}>
          <ButtonAnexarArquivos
            buttonText=""
            accept="image/*"
            maxFileSizeMb={25}
            currentFilesLength={0}
            variant="ghost"
            onAnexar={(files) => {
              if (files?.length) {
                handleUpload(files[0]);
              }
            }}
          />

          <Button onClick={() => setModalCameraOpen(true)}>
            <AiOutlineCamera color={colors.primary} size={20} />
          </Button>

          {fileInfo ? (
            <Button as="a" target="_blank" href={fileInfo}>
              <FaExternalLinkAlt color={colors.primary} size={20} />
            </Button>
          ) : null}
        </Flex>
      ) : null}

      {modalCameraOpen ? (
        <ModalCamera
          visible={!!modalCameraOpen}
          onClose={() => setModalCameraOpen(false)}
          onSubmit={(base64) => {
            const file = base64ToFile(base64);

            handleUpload(file);
          }}
        />
      ) : null}
    </Container>
  );
}

export default UploadImagem;
